import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daimlfjoh653ivfvogkg";

type Status = "idle" | "loading" | "success" | "error";

export default function SubmitForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    data.delete("website_alt");
    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const codeOk = parsed?.code === "OK";
      const isSpam = /spam/i.test(serverMsg);

      if (response.ok && codeOk && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg && !isSpam ? serverMsg : "提交没有成功，请稍后再试一次。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接出现问题，请稍后重试。");
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-6 text-center md:p-8">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-check-double-line text-2xl"></i>
        </div>
        <h3 className="font-heading text-lg font-600 text-foreground-950">线索已收到</h3>
        <p className="mt-2 text-sm text-foreground-600">
          谢谢你提供的信息，核实组会逐条查看，可核实的内容会进入鉴定流程。
        </p>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="17baoliao-submit"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col">
          <label htmlFor="nickname" className="mb-1.5 text-sm font-500 text-foreground-800">
            称呼
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            placeholder="可匿名，如“热心读者”"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="email" className="mb-1.5 text-sm font-500 text-foreground-800">
            联系邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="用于回访核实，不会公开"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col">
        <label htmlFor="topic" className="mb-1.5 text-sm font-500 text-foreground-800">
          线索类型
        </label>
        <select
          id="topic"
          name="topic"
          defaultValue="真假鉴定"
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          <option value="真假鉴定">真假鉴定</option>
          <option value="辟谣求助">辟谣求助</option>
          <option value="后续回应">后续回应</option>
          <option value="反转进展">反转进展</option>
          <option value="订阅更新">订阅更新</option>
        </select>
      </div>

      <div className="mt-4 flex flex-col">
        <label htmlFor="message" className="mb-1.5 text-sm font-500 text-foreground-800">
          内容说明
        </label>
        <textarea
          id="message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请描述你看到的传言、原始链接或时间点，越具体越容易核实（不超过 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-guest-ref"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-accent-700">
          <i className="ri-error-warning-line"></i>
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 sm:w-auto"
      >
        {status === "loading" ? "正在提交…" : "提交线索"}
      </button>
      <p className="mt-3 text-xs leading-relaxed text-foreground-500">
        请勿提交涉及他人隐私或未经证实的敏感信息；提交即表示同意我们在核实范围内使用该线索。
      </p>
    </form>
  );
}